import { GaugeChart } from '../charts';
import { AlertTriangle, CheckCircle, XCircle, RefreshCw, Shield } from 'lucide-react';

interface SLAMetric {
  name: string;
  value: number;
  target: number;
}

interface SLAComplianceWidgetProps {
  title?: string;
  overallCompliance?: number;
  metrics?: SLAMetric[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
}

// Mock data for development
const mockMetrics: SLAMetric[] = [
  { name: 'First Response', value: 92, target: 90 },
  { name: 'Resolution Time', value: 88, target: 85 },
  { name: 'Customer Satisfaction', value: 94, target: 90 },
];

export function SLAComplianceWidget({
  overallCompliance,
  metrics,
  loading = false,
  error,
  onRetry,
  className = '',
  title,
}: SLAComplianceWidgetProps) {
  if (error) {
    return (
      <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <AlertTriangle className="h-8 w-8 text-red-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{error}</p>
          {onRetry && (
            <button onClick={onRetry} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 dark:bg-primary-900/20 dark:text-primary-400 rounded-lg hover:bg-primary-100 transition-colors">
              <RefreshCw className="h-3 w-3" /> Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse" />
        </div>
        <div className="flex-1 min-h-0 p-3 flex items-center justify-center">
          <div className="h-32 w-32 bg-gray-100 dark:bg-gray-700 rounded-full animate-pulse" />
        </div>
      </div>
    );
  }

  // Empty state when no SLA data exists at all (null/undefined means no data; 0 means 0% compliance which IS valid data)
  const hasNoData = !metrics || metrics.length === 0 || (overallCompliance == null && metrics.every(m => m.value == null));
  if (hasNoData) {
    return (
      <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">SLA Compliance</h3>
        </div>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <Shield className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No SLA Data</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">No SLA compliance data available for this period</p>
        </div>
      </div>
    );
  }

  // Round percentages for display (avoid long floats like 18.219461697722565%).
  const fmtPct = (v: number) => `${Math.round(v * 10) / 10}`;

  const getStatusIcon = (value: number, target: number) => {
    const diff = value - target;
    if (diff >= 5) return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    if (diff >= 0) return <CheckCircle className="w-3.5 h-3.5 text-yellow-500" />;
    return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  };

  // Only show alert when there's actual SLA data and compliance is genuinely low
  const hasSlAData = metrics.some((m) => m.value > 0);

  return (
    <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {title || 'SLA Compliance'}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Overall performance against targets
        </p>
      </div>
      <div className="flex-1 min-h-0 p-3">
        <GaugeChart
          value={Math.round((overallCompliance ?? 0) * 10) / 10}
          label="Overall"
         
          thresholds={{ warning: 80, danger: 70 }}
        />
      </div>
      <div className="px-4 pb-2 space-y-1.5 shrink-0">
        {metrics.map((metric) => (
          <div key={metric.name} className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {getStatusIcon(metric.value, metric.target)}
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {metric.name}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {fmtPct(metric.value)}%
              </span>
              <span className="text-[11px] text-gray-400 dark:text-gray-500">
                / {metric.target}%
              </span>
            </div>
          </div>
        ))}
      </div>
      {hasSlAData && (overallCompliance ?? 0) < 70 && (
        <div className="px-4 pb-3 shrink-0">
          <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
            <span className="text-xs text-red-700 dark:text-red-400">
              SLA compliance is below acceptable threshold
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
